import React, { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";

const CheckOut = () => {
  const cart = useSelector(state => state.cart);
  const [step, setStep] = useState(1); // 1: Information, 2: Shipping
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    agreeToTerms: false,
    keepUpdated: false,
    address1: "",
    address2: "",
    city: "",
    postal: "",
    shippingMethod: "standard",
    useLocation: false,
  });

  const [errors, setErrors] = useState({});

  const validateInformation = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/[^\d]/g, ''))) {
      newErrors.phone = "Phone number must be 10 digits";
    }
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateShipping = () => {
    const newErrors = {};
    if (!formData.address1.trim()) newErrors.address1 = "Address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.postal.trim()) newErrors.postal = "Postal code is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleNextStep = () => {
    if (step === 1 && validateInformation()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async () => {
    if (validateShipping()) {
      try {
        toast.loading('Processing payment...');

        // Prepare payload for Stripe
        const payload = {
          items: cart.items.map(item => ({
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            color: item.color,
            size: item.size
          })),
          customerInfo: {
            ...formData
          }
        };

        // Save order data to localStorage
        localStorage.setItem('pendingOrder', JSON.stringify({
          orderData: payload,
          timestamp: new Date().toISOString()
        }));

        // Create Stripe checkout session
        const response = await fetch(`http://localhost:4000/create-checkout-session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            price: cart.items.reduce((acc, item) => acc + (item.price * item.quantity), 0),
            quantity: cart.items.reduce((acc, item) => acc + item.quantity, 0),
            productName: cart.items.map(item => item.name).join(', ')
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to create checkout session");
        }

        const data = await response.json();

        // Redirect to Stripe checkout
        setTimeout(() => {
          window.location.href = data.url;
        }, 1000);

      } catch (error) {
        console.error('Checkout error:', error);
        toast.dismiss(); // Dismiss the loading toast
        toast.error('Payment processing failed. Please try again.');
        localStorage.removeItem('pendingOrder'); // Clean up on error
      }
    }
  };

  const calculateTotal = () => {
    return cart.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleLocationToggle = async (e) => {
    const useLocation = e.target.checked;
    setFormData(prev => ({ ...prev, useLocation }));

    if (useLocation) {
      setIsLoadingLocation(true);
      try {
        // Get user's location
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });

        const { latitude, longitude } = position.coords;

        // Use Nominatim OpenStreetMap for reverse geocoding
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
          {
            headers: {
              'Accept-Language': 'en-US,en;q=0.9',
            },
          }
        );

        if (!response.ok) throw new Error('Failed to fetch address');

        const data = await response.json();
        const address = data.address;

        // Construct address line 1
        const addressLine1Parts = [
          address.house_number,
          address.road || address.street,
          address.suburb
        ].filter(Boolean);

        // Construct address line 2
        const addressLine2Parts = [
          address.neighbourhood,
          address.district,
          address.subdistrict
        ].filter(Boolean);

        // Update form with location data
        setFormData(prev => ({
          ...prev,
          address1: addressLine1Parts.join(', '),
          address2: addressLine2Parts.join(', '),
          city: address.city || address.town || address.village || address.municipality || '',
          postal: address.postcode || '',
        }));

        toast.success('Location detected successfully!');
      } catch (error) {
        console.error('Location error:', error);
        toast.error('Failed to detect location. Please enter manually.');
        setFormData(prev => ({ ...prev, useLocation: false }));
      } finally {
        setIsLoadingLocation(false);
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex gap-12">
        {/* Left Section */}
        <div className="flex-1">
          <h1 className="text-2xl font-semibold mb-6">CHECKOUT</h1>

          {/* Progress Steps */}
          <div className="flex items-center mb-8">
            <div className={`rounded-full px-6 py-2 ${step === 1 ? 'bg-purple-500 text-white' : 'bg-purple-500 text-white'}`}>
              Information
            </div>
            <div className="flex-1 h-1 bg-purple-500"></div>
            <div className={`rounded-full px-6 py-2 ${step === 2 ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
              Shipping
            </div>
          </div>

          {/* Form Steps */}
          {step === 1 ? (
            <div className="space-y-6">
              {/* Information Form */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                  {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                  {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                />
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
              </div>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-purple-500 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">I agree to the terms and conditions</span>
                </label>
                {errors.agreeToTerms && <p className="text-sm text-red-600">{errors.agreeToTerms}</p>}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="keepUpdated"
                    checked={formData.keepUpdated}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-purple-500 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Keep me updated on news and exclusive offers</span>
                </label>
              </div>
              <button
                onClick={handleNextStep}
                className="w-full bg-purple-500 text-white py-3 rounded-md hover:bg-purple-600 transition-colors"
              >
                Continue to Shipping
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Shipping Form */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
                <input
                  type="text"
                  name="address1"
                  value={formData.address1}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                />
                {errors.address1 && <p className="mt-1 text-sm text-red-600">{errors.address1}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
                <input
                  type="text"
                  name="address2"
                  value={formData.address2}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                  {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                  <input
                    type="text"
                    name="postal"
                    value={formData.postal}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                  {errors.postal && <p className="mt-1 text-sm text-red-600">{errors.postal}</p>}
                </div>
              </div>
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="useLocation"
                    checked={formData.useLocation}
                    onChange={handleLocationToggle}
                    className="rounded border-gray-300 text-purple-500 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Use my current location</span>
                </label>
                {isLoadingLocation && (
                  <div className="text-sm text-gray-500">Detecting your location...</div>
                )}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={handleBack}
                  className="flex-1 border border-gray-300 py-3 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 bg-purple-500 text-white py-3 rounded-md hover:bg-purple-600 transition-colors"
                >
                  Pay Now
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Section - Order Summary */}
        <div className="w-96">
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-medium mb-6 text-purple-600">YOUR ORDER ({cart.items.reduce((acc, item) => acc + item.quantity, 0)})</h2>
            <div className="space-y-4">
              {cart.items.map(item => (
                <div key={`${item.id}-${item.color}-${item.size}`} className="flex items-center gap-4 bg-white p-4 rounded-lg">
                  <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                  <div className="flex-1">
                    <h3 className="font-medium">{item.name}</h3>
                    <p className="text-purple-500">₹{item.price}</p>
                    {item.color && <p className="text-sm text-gray-500">Color: {item.color}</p>}
                    {item.size && <p className="text-sm text-gray-500">Size: {item.size}</p>}
                    <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t">
              <div className="flex justify-between text-lg font-medium">
                <span>Total</span>
                <span>₹{calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckOut; 